import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

// Lucide React Icons
import {
  ArrowUp,
  ArrowDown,
  Trash2,
  Edit,
  Clock,
  Image as ImageIcon,
  Video,
  AlertCircle,
  CheckCircle,
  XCircle,
  Upload,
  X,
  Menu,
  ChevronUp,
  ChevronDown,
  Heart,
  Eye,
  ShoppingCart,
  Star,
  Info,
  Camera,
  Play,
  Plus,
  Save,
  RefreshCw,
  Settings,
  Home,
  LogOut,
  User,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Linkedin,
  Github,
  Moon,
  Sun,
  Bell,
  Search,
  Filter,
  Download,
  Upload as UploadIcon,
  FileText,
  Folder,
  Tag,
  DollarSign,
  Percent,
  Shield,
  Truck,
  Package,
  Box,
  Grid,
  List,
  Layers,
  Layout,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  PlayCircle,
  PauseCircle,
  SkipBack,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  MoreHorizontal,
  Settings2,
  Menu as MenuIcon,
  X as XIcon
} from "lucide-react";

function AdminHomeBanner() {
  // ===== State for banners list =====
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [reordering, setReordering] = useState(false);

  // ===== Form fields =====
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [sortOrder, setSortOrder] = useState("1");
  const [isActive, setIsActive] = useState(true);

  // ===== Media =====
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  
  // ===== Drag and drop state =====
  const [isDragActive, setIsDragActive] = useState(false);
  const dropZoneRef = useRef(null);

  // ===== UI state =====
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ===== GSAP Refs =====
  const pageRef = useRef(null);
  const listRef = useRef(null);

  // ===== Helpers =====
  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      ".toast-anim",
      { y: 12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.25, ease: "power2.out" }
    );
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  };

  // ===== Check if sort order is available =====
  const isSortOrderAvailable = (order, excludeId = null) => {
    const numOrder = parseInt(order, 10);
    if (isNaN(numOrder) || numOrder < 1) return false;
    
    return !banners.some(banner => 
      Number(banner.sort_order) === numOrder &&
      (excludeId === null || Number(banner.id) !== Number(excludeId))
    );
  };

  // ===== Get next available sort order =====
  const getNextAvailableSortOrder = () => {
    const maxOrder = banners.reduce(
      (max, banner) => Math.max(max, Number(banner.sort_order) || 0),
      0
    );

    for (let i = 1; i <= maxOrder + 1; i++) {
      if (isSortOrderAvailable(i)) {
        return i.toString();
      }
    }

    return String(maxOrder + 1);
  };

  // ===== Handle sort order change =====
  const handleSortOrderChange = (e) => {
    const value = e.target.value;
    
    if (value === '') {
      setSortOrder('');
      return;
    }
    
    const numericValue = value.replace(/[^0-9]/g, '');
    
    if (numericValue !== '') {
      setSortOrder(parseInt(numericValue, 10).toString());
    }
  };

  const handleSortOrderBlur = () => {
    if (sortOrder === '') {
      setSortOrder(getNextAvailableSortOrder());
      return;
    }
    
    const numOrder = parseInt(sortOrder, 10);
    if (numOrder < 1) {
      setSortOrder("1");
    }
    
    const finalValue = parseInt(sortOrder, 10);
    if (!isNaN(finalValue) && !isSortOrderAvailable(finalValue, editingBanner?.id)) {
      showToast("error", `Sort order ${finalValue} is already taken. Please choose another number.`);
    }
  };

  // ===== Drag and drop handlers =====
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // ===== Process file =====
  const processFile = (file) => {
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      showToast("error", "Please select a valid image or video.");
      return;
    }

    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast("error", `File too large. Max ${isVideo ? '50MB' : '5MB'} for ${isVideo ? 'videos' : 'images'}.`);
      return;
    }

    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);

    const url = URL.createObjectURL(file);
    setMediaFile(file);
    setMediaPreviewUrl(url);
  };

  // ===== Handle file upload =====
  const handleMediaUpload = (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    if (!file) return;

    processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeMedia = () => {
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(null);
    setMediaPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ===== Fetch banners =====
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}/getHomeBanners.php?admin=true`);
      console.log(response)
      if (response?.success) {
        setBanners(response.banners || []);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      showToast("error", "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // ===== Update default sort order when banners change =====
  useEffect(() => {
    if (!editingBanner) {
      setSortOrder(getNextAvailableSortOrder());
    }
  }, [banners, editingBanner]);

  // ===== Reset form =====
  const resetForm = () => {
    setTitle("");
    setSubtitle("");
    setSortOrder(getNextAvailableSortOrder());
    setIsActive(true);
    removeMedia();
    setEditingBanner(null);
    setMobileMenuOpen(false);
  };

  // ===== Edit banner =====
  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setTitle(banner.title || "");
    setSubtitle(banner.subtitle || "");
    setSortOrder(banner.sort_order?.toString() || "1");
    setIsActive(banner.is_active == 1);
    
    setMediaPreviewUrl(banner.media_url);
    setMediaFile(null);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // ===== Delete banner =====
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;

    try {
      const response = await $.ajax({
        url: `${ApiURl}/deleteHomeBanner.php?id=${id}`,
        type: 'DELETE',
        dataType: 'json'
      });

      if (response?.success) {
        showToast("success", "Banner deleted successfully");
        await fetchBanners();
        setSortOrder(getNextAvailableSortOrder());
      } else {
        showToast("error", response?.message || "Failed to delete banner");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      showToast("error", "Server error while deleting banner");
    }
  };

  // ===== Submit form =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sortOrder === '') {
      showToast("error", "Please enter a sort order.");
      return;
    }

    const numSortOrder = parseInt(sortOrder, 10);

    if (isNaN(numSortOrder) || numSortOrder < 1) {
      showToast("error", "Sort order must be 1 or higher.");
      return;
    }

    if (!mediaFile && !editingBanner) {
      showToast("error", "Please upload an image or video.");
      return;
    }

    if (!isSortOrderAvailable(numSortOrder, editingBanner?.id)) {
      showToast("error", `Sort order ${numSortOrder} is already taken. Please choose another number.`);
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    if (mediaFile) {
      formData.append("media", mediaFile);
    }
    formData.append("title", title || "");
    formData.append("subtitle", subtitle || "");
    formData.append("sort_order", numSortOrder.toString());
    formData.append("is_active", isActive ? "1" : "0");

    const url = editingBanner 
      ? `${ApiURl}/updateHomeBanner.php?id=${editingBanner.id}`
      : `${ApiURl}/addHomeBanner.php`;

    try {
      const response = await $.ajax({
        url: url,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json"
      });

      if (response?.success) {
        showToast("success", editingBanner ? "Banner updated successfully" : response.message || "Banner added successfully");
        resetForm();
        await fetchBanners();
      } else {
        showToast("error", response?.message || "Failed to save banner");
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      showToast("error", "Server error while saving banner");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Move banner up/down using dedicated reorder API =====
  const moveBanner = async (id, direction) => {
    const orderedBanners = [...banners].sort(
      (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)
    );
    const currentIndex = orderedBanners.findIndex(b => b.id === id);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= orderedBanners.length) return;
    
    const banner1 = orderedBanners[currentIndex];
    const banner2 = orderedBanners[newIndex];
    
    setReordering(true);
    
    try {
      const response = await $.ajax({
        url: `${ApiURl}/reorderBanners.php`,
        type: 'POST',
        data: {
          banner1_id: banner1.id,
          banner2_id: banner2.id,
          sort_order1: banner2.sort_order,
          sort_order2: banner1.sort_order
        },
        dataType: 'json'
      });

      if (response?.success) {
        showToast("success", "Banner order updated");
        await fetchBanners();
      } else {
        showToast("error", response?.message || "Failed to reorder banners");
      }
    } catch (error) {
      console.error("Error reordering banners:", error);
      showToast("error", "Failed to reorder banners");
    } finally {
      setReordering(false);
    }
  };

  const isVideoSelected = mediaPreviewUrl && (
    mediaFile?.type?.startsWith("video/") || 
    (!mediaFile && mediaPreviewUrl.match(/\.(mp4|webm|ogg)$/i))
  );

  const canAddMore = true;

  const isSortOrderValid = () => {
    if (sortOrder === '') return false;
    const numOrder = parseInt(sortOrder, 10);
    if (isNaN(numOrder) || numOrder < 1) return false;
    return isSortOrderAvailable(numOrder, editingBanner?.id);
  };

  return (
    <div ref={pageRef} className="admin-home-banner-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] font-[Kanit] text-[#2B0000]">
      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-anim fixed bottom-4 right-4 z-[9999]">
          <div
            className={[
              "flex items-center gap-2 rounded-xl px-4 py-3 shadow-lg border text-sm",
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "border-[#d4af37]/25 bg-[#fff7e6] text-[#7d5a19]",
            ].join(" ")}
          >
            {toast.type === "success" ? (
              <CheckCircle className="w-4 h-4 text-emerald-600" />
            ) : toast.type === "error" ? (
              <XCircle className="w-4 h-4 text-rose-600" />
            ) : (
              <Info className="w-4 h-4 text-slate-600" />
            )}
            <span>{toast.msg}</span>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[1500px] px-3 py-4 sm:px-5 sm:py-6 lg:px-7">
        {/* Header with Gradient */}
        <div className="mb-6 rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl border border-[#d4af37]/25 bg-[#2B0000] p-3 shadow-[0_14px_30px_rgba(43,0,0,0.18)]">
                <ImageIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">
                  Home Banner Management
                </h1>
                <p className="mt-1 text-sm text-[#6f5e57]">
                  Manage homepage banners. Active banners appear in sort order.
                </p>
              </div>
            </div>
            
            {/* Mobile Menu Toggle */}
            {canAddMore && !editingBanner && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-medium text-[#f4dfae] shadow-md transition hover:bg-[#4A090B] sm:hidden"
              >
                {mobileMenuOpen ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {mobileMenuOpen ? 'Close Form' : 'Add Banner'}
              </button>
            )}
          </div>
          
          {/* Stats Cards */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-[18px] border border-[#decfc3] bg-white p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <ImageIcon className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#75645d]">Total Banners</p>
                  <p className="font-serif text-2xl font-medium text-[#2B0000]">{banners.length}</p>
                </div>
              </div>
            </div>
            
            <div className="rounded-[18px] border border-[#decfc3] bg-white p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#75645d]">Active</p>
                  <p className="font-serif text-2xl font-medium text-[#47704e]">
                    {banners.filter(b => b.is_active == 1).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="rounded-[18px] border border-[#decfc3] bg-white p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#75645d]">Videos</p>
                  <p className="font-serif text-2xl font-medium text-[#8a651b]">
                    {banners.filter(b => b.media_type === 'video').length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="rounded-[18px] border border-[#decfc3] bg-white p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Clock className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[#75645d]">Next Order</p>
                  <p className="font-serif text-2xl font-medium text-[#7b5a84]">#{getNextAvailableSortOrder()}</p>
                </div>
              </div>
            </div>
          </div>
          
          {false && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-700">
                Expired banner cleanup is disabled.
              </p>
            </div>
          )}
          
          {false && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
              <Info className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-700">
                Available sort orders: {[].map(o => (
                  <span key={o} className="inline-flex items-center ml-2 px-2 py-1 bg-white rounded-lg text-xs font-medium">
                    #{o}
                  </span>
                ))}
              </p>
            </div>
          )}
        </div>

        {/* Form Card with Animation */}
        {(canAddMore || editingBanner) && (
          <div
            className={`
              mb-8 overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)] transition-all duration-300
              ${!editingBanner && !mobileMenuOpen ? 'hidden sm:block' : 'block'}
            `}
          >
            <div className="border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-6 py-4">
              <div className="flex items-center gap-2">
                {editingBanner ? (
                  <Edit className="w-5 h-5 text-[#b29063]" />
                ) : (
                  <Plus className="w-5 h-5 text-[#b29063]" />
                )}
                <h2 className="font-serif text-xl font-medium text-[#2B0000]">
                  {editingBanner ? "Edit Banner" : "Add New Banner"}
                </h2>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Form Fields */}
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#4a352f]">
                      Title <span className="text-slate-400">(Optional)</span>
                    </label>
                    <div className="relative">
                      <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9b877e]" />
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full rounded-xl border border-[#d6c4b5] bg-white py-3 pl-10 pr-4 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                        placeholder="Banner title"
                        maxLength="100"
                      />
                    </div>
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-[#4a352f]">
                      Subtitle <span className="text-slate-400">(Optional)</span>
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9b877e]" />
                      <input
                        type="text"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        className="w-full rounded-xl border border-[#d6c4b5] bg-white py-3 pl-10 pr-4 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                        placeholder="Banner subtitle"
                        maxLength="200"
                      />
                    </div>
                  </div>

                  {/* Sort Order & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#4a352f]">
                        Sort Order <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Layers className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9b877e]" />
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={sortOrder}
                          onChange={handleSortOrderChange}
                          onBlur={handleSortOrderBlur}
                          className="w-full rounded-xl border border-[#d6c4b5] bg-white py-3 pl-10 pr-4 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          placeholder="1"
                        />
                      </div>
                      <p className="mt-2 text-xs text-[#7d6c64]">
                        Any number 1 or higher
                      </p>
                      {sortOrder !== '' && !isSortOrderAvailable(parseInt(sortOrder, 10), editingBanner?.id) && (
                        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Order {sortOrder} is taken
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-[#4a352f]">Status</label>
                      <div className="flex items-center h-[46px]">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={(e) => setIsActive(e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`w-10 h-6 rounded-full transition-colors ${isActive ? 'bg-[#8f6f38]' : 'bg-[#d9c9bc]'}`}>
                              <div className={`absolute w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform top-1 ${isActive ? 'translate-x-5' : 'translate-x-1'}`} />
                            </div>
                          </div>
                          <span className="text-sm text-[#4f3d36]">{isActive ? 'Active' : 'Inactive'}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - File Upload with Drag & Drop */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#4a352f]">
                    Media File <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Drag & Drop Zone */}
                  <div
                    ref={dropZoneRef}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`mb-4 flex w-full cursor-pointer flex-col items-center justify-center rounded-[20px] border-2 border-dashed p-8 text-center transition-all ${
                      isDragActive
                        ? "scale-[1.02] border-[#c59a4a] bg-[#fff4d9]"
                        : "border-[#d4af37]/35 bg-[#fbf3eb] hover:border-[#c59a4a] hover:bg-[#fff7e8]"
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className={`rounded-2xl border border-[#d8c6b7] bg-white p-4 shadow-sm transition-all ${isDragActive ? 'scale-110' : ''}`}>
                        {isDragActive ? (
                          <UploadIcon className="w-8 h-8 text-[#b29063]" />
                        ) : (
                          <Upload className="w-8 h-8 text-slate-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-[#4f3d36]">
                          <span className="font-semibold text-[#8f6f38]">
                            {isDragActive ? "Drop it here!" : "Click to upload"}
                          </span>
                          {!isDragActive && <span className="text-[#75645d]"> or drag and drop</span>}
                        </p>
                        <p className="mt-2 text-xs text-[#7d6c64]">Images (JPEG, PNG, GIF, WebP)</p>
                        <p className="text-xs text-[#7d6c64]">Videos (MP4, WebM) - Max 50MB</p>
                      </div>
                    </div>

                    <input
                      ref={fileInputRef}
                      id="banner-file"
                      type="file"
                      className="hidden"
                      onChange={handleMediaUpload}
                      accept="image/*,video/*"
                    />
                  </div>

                  {mediaPreviewUrl && (
                    <div className="mt-4">
                      <div className="overflow-hidden rounded-[18px] border border-[#d8c6b7] bg-white shadow-[0_16px_40px_rgba(43,0,0,0.08)]">
                        <div className="relative group">
                          {isVideoSelected ? (
                            <video
                              src={mediaPreviewUrl}
                              controls
                              className="h-52 w-full bg-black object-contain"
                            />
                          ) : (
                            <img
                              src={mediaPreviewUrl}
                              alt="Banner Preview"
                              className="h-52 w-full bg-[#fffaf5] object-contain"
                            />
                          )}

                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              type="button"
                              onClick={removeMedia}
                              className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-5 h-5 text-red-600" />
                            </button>
                          </div>

                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/90 rounded-lg text-xs font-medium">
                              {isVideoSelected ? (
                                <>
                                  <Video className="w-3 h-3 text-[#b29063]" />
                                  Video
                                </>
                              ) : (
                                <>
                                  <ImageIcon className="w-3 h-3 text-[#b29063]" />
                                  Image
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      type="submit"
                      disabled={submitting || !isSortOrderValid()}
                      className={[
                        "flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-all",
                        (submitting || !isSortOrderValid()) 
                          ? "cursor-not-allowed bg-[#b7a69b] text-white" 
                          : "bg-[#2B0000] text-[#f4dfae] hover:-translate-y-0.5 hover:bg-[#4A090B]",
                      ].join(" ")}
                    >
                      {submitting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {editingBanner ? "Update Banner" : "Add Banner"}
                        </>
                      )}
                    </button>
                    
                    {editingBanner && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="flex items-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-5 py-3 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Banners List */}
        <div className="overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
          <div className="border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layout className="w-5 h-5 text-[#b29063]" />
                <h2 className="font-serif text-xl font-medium text-[#2B0000]">Current Banners</h2>
              </div>
              {reordering && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Reordering...
                </div>
              )}
            </div>
            <p className="mt-1 text-sm text-[#6f5e57]">Use the arrows to reorder banners.</p>
          </div>

          <div ref={listRef} className="p-4 sm:p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <RefreshCw className="mb-4 h-8 w-8 animate-spin text-[#8f6f38]" />
                <p className="text-sm text-slate-600">Loading banners...</p>
              </div>
            ) : banners.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="mx-auto mb-4 h-16 w-16 text-[#d0c1b5]" />
                <p className="text-sm text-slate-600">No banners added yet.</p>
                {canAddMore && (
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm text-[#f4dfae] transition hover:bg-[#4A090B]"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your First Banner
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {[...banners].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((banner, index) => {
                  return (
                    <div
                      key={banner.id}
                      className="group relative flex flex-col items-start gap-4 rounded-[20px] border border-[#decfc3] bg-white p-4 transition-all hover:border-[#c59a4a]/40 hover:shadow-[0_16px_36px_rgba(43,0,0,0.09)] sm:flex-row"
                    >
                      {/* Reorder Arrows - Better positioned */}
                      <div className="absolute right-2 top-2 flex gap-1 rounded-lg bg-white p-1 shadow-sm sm:relative sm:right-auto sm:top-auto sm:flex-col sm:bg-transparent sm:p-0 sm:shadow-none">
                        <button
                          onClick={() => moveBanner(banner.id, 'up')}
                          disabled={index === 0 || reordering}
                          className={[
                            "p-2 rounded-lg transition-all",
                            index === 0 || reordering
                              ? "text-slate-300 cursor-not-allowed" 
                              : "text-[#6f5e57] hover:bg-[#2B0000] hover:text-[#f4dfae] hover:shadow-md"
                          ].join(" ")}
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveBanner(banner.id, 'down')}
                          disabled={index === banners.length - 1 || reordering}
                          className={[
                            "p-2 rounded-lg transition-all",
                            index === banners.length - 1 || reordering
                              ? "text-slate-300 cursor-not-allowed" 
                              : "text-[#6f5e57] hover:bg-[#2B0000] hover:text-[#f4dfae] hover:shadow-md"
                          ].join(" ")}
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Preview with Badge */}
                      <div className="relative h-20 w-24 flex-shrink-0 overflow-hidden rounded-xl border border-[#d8c6b7] bg-white shadow-md">
                        {banner.media_type === 'video' ? (
                          <video src={banner.media_url} className="w-full h-full object-cover" />
                        ) : (
                          <img src={banner.media_url} alt={banner.title} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute top-1 left-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/90 rounded text-[10px] font-medium">
                            #{banner.sort_order}
                          </span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-serif text-lg font-medium text-[#2B0000]">
                            {banner.title || 'Untitled Banner'}
                          </h3>
                          <span className={[
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
                            banner.is_active == 1 
                              ? "border border-[#cfe2d1] bg-[#edf7ee] text-[#47704e]" 
                              : "border border-[#dfd1c6] bg-[#f3ebe4] text-[#6d5a53]"
                          ].join(" ")}>
                            {banner.is_active == 1 ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                Active
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Inactive
                              </>
                            )}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#d4af37]/25 bg-[#fff7e6] px-2 py-0.5 text-xs font-medium text-[#7d5a19]">
                            <Layers className="w-3 h-3" />
                            Order #{banner.sort_order}
                          </span>
                        </div>
                        
                        {banner.subtitle && (
                          <p className="mt-2 line-clamp-2 text-sm text-[#6f5e57]">{banner.subtitle}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-4 mt-3 text-xs">
                          <span className="flex items-center gap-1 text-[#7d6c64]">
                            {banner.media_type === 'video' ? (
                              <Video className="w-3 h-3" />
                            ) : (
                              <ImageIcon className="w-3 h-3" />
                            )}
                            {banner.media_type}
                          </span>
                        </div>
                        
                        <div className="mt-3">
                          {banner.is_active == 1 ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#47704e]">
                              <CheckCircle className="w-3 h-3" />
                              Currently active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#7d6c64]">
                              <XCircle className="w-3 h-3" />
                              Manually inactive
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 w-full sm:w-auto justify-end">
                        <button
                          onClick={() => handleEdit(banner)}
                          className={[
                            "p-2 rounded-lg transition-all flex items-center gap-1 text-sm",
                            "border border-[#d6c4b5] bg-white text-[#2B0000] hover:border-[#2B0000] hover:bg-[#2B0000] hover:text-[#f4dfae] hover:shadow-md"
                          ].join(" ")}
                          title="Edit banner"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="sm:hidden">Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(banner.id)}
                          className="flex items-center gap-1 rounded-lg border border-red-200 bg-[#fff4f1] p-2 text-sm text-red-600 transition-all hover:bg-red-600 hover:text-white hover:shadow-md"
                          title="Delete banner"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="sm:hidden">Delete</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .admin-home-banner-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .admin-home-banner-luxury input,
        .admin-home-banner-luxury button {
          font: inherit;
        }

        .admin-home-banner-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .admin-home-banner-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .admin-home-banner-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .admin-home-banner-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .admin-home-banner-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .admin-home-banner-luxury {
            padding-bottom: 18px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .admin-home-banner-luxury *,
          .admin-home-banner-luxury *::before,
          .admin-home-banner-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminHomeBanner;